 
import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Table,
  Tooltip,
  OverlayTrigger,
  Modal,
  Button,
  Form,
  Spinner,
} from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import SVG from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import {
  Card,
  CardBody,
  CardHeader,
  LoadingDialog,
  CardHeaderToolbar,
} from '../../../_metronic/_partials/controls';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import { getSiteBaseURL, getDemoMode, decryptedData } from '../../../helpers';
import {
  getTransactions,
  getTransactionsCSVList,
} from '../../../store/actions';
import moment from 'moment';
import { formatCurrency } from '../../../helpers/numberFormat';
import SearchTransaction from './SearchTransaction';
import TotalTransaction from './TotalTransaction';
import { authCheckState } from '../../../store/actions/index';
import * as actions from '../../../store/actions/index';
import { abi } from '../../../config/abi';
import { connectionContext } from '../../connect-wallet/ConnectionStatus';
import useContractInteraction from '../../../helpers/useContractInteraction';
import { useAppKit } from '@reown/appkit/react';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import {
  useGetTransactions,
  useUpdateTransaction,
} from '../../../swrHelpers/transactions';
import PaymentGatewayLogo from '../../components/common/PaymentGatewaysLogo/PaymentGatewaysLogo';
import SortDropdownHeader, {
  sortData,
} from '../../components/common/SortDropdownHeader/SortDropdownHeader';
import { CurrencyLogo } from '../../../helpers/CryptoCurrencySymbol';

function Transaction() {
  const navigate = useNavigate();
  const demoMode = getDemoMode();
  const { open } = useAppKit();
  const { address, isConnected, isReconnecting, isConnecting } =
    useContext(connectionContext);
  const { isLoading, interact, isSuccess } = useContractInteraction();
  const siteBaseUrl = useRef(getSiteBaseURL());
  const methods = useForm({ mode: 'onTouched' });
  const dispatch = useDispatch();
  const intl = useIntl();
  const [refund, setRefund] = useState(false);
  const [show, setShow] = useState(false);
  const [transactionInfo, setTransactionInfo] = useState();
  const [showMore, setShowMore] = useState(1);
  const [TransactionFilterData, setTransactionFilterData] = useState();
  const [csvFrontTransactionList, setCsvFrontTransactionList] = useState([]);
  const isEncrypted = useRef(
    import.meta.env.VITE_DATABASE_ENCRYPTION === 'true',
  );
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm();

  useEffect(() => {
    dispatch(authCheckState());
    dispatch(getTransactions(1));
    dispatch(getTransactionsCSVList(''));
  }, [dispatch]);

  // const { transactions } = useSelector((state) => state.transaction);
  const { generalSettings } = useSelector((state) => state.settings);
  const { payments } = useSelector((state) => state.paymentGateway);
  const { exportTransactionsList, exportLoading } = useSelector(
    (state) => state.exportAllCSV,
  );
  const { currentPermissions } = useSelector((state) => state.role);

  const [activeFilters, setActiveFilters] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null,
  });
  const { transactionsData: transactions, isValidating } = useGetTransactions(
    showMore,
    activeFilters,
  );

  const { trigger: triggerUpdateTransaction, data: updatedTransactionData } =
    useUpdateTransaction();

  const onSubmit = (data) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTransactionFilterData(data);
    setActiveFilters(data);
    setShowMore(1);
    dispatch(getTransactionsCSVList(data));
  };

  const loadMore = () => {
    setShowMore(showMore + 1);
  };

  const showTransactionDetail = (data) => {
    setShow(true);
    setTransactionInfo(data);
  };

  const handleClose = () => {
    setShow(false);
    setTransactionInfo();
  };

  const refundShow = (data) => {
    setRefund(true);
    setTransactionInfo(data);
    setValue('tranId', data?._id);
    setValue('preapprovalStatus', 'REFUNDED');
    setValue('paymentStatus', 3);
  };

  const refundClose = () => {
    setRefund(false);
    reset();
  };

  const onSubmitRefund = (data) => {
    data.createdAt = Date.now();
    triggerUpdateTransaction(data);
    // dispatch(updateTransaction(data));
    setRefund(false);
    reset();
  };

  const transactionCSVHeaders = [
    { label: 'Project', key: 'campaignName' },
    { label: 'Investor Name', key: 'investorName' },
    { label: 'Invest Type', key: 'purchaseType' },
    { label: 'Amount', key: 'preapprovalTotalAmount' },
    { label: 'Revenue', key: 'earnedFee' },
    { label: 'Earned Fees Percentage', key: 'earnedFeesPercentage' },
    { label: 'Earned Flat Fees', key: 'earnedFlatFees' },
    { label: 'Gateway Name', key: 'gatewayName' },
    { label: 'Gateway Transaction ID', key: 'paymentConfirmationId' },
    { label: 'Status', key: 'preapprovalStatus' },
    { label: 'Date', key: 'createdAt' },
  ];

  useEffect(() => {
    if (exportTransactionsList?.length > 0) {
      let result = exportTransactionsList?.map((transaction) => ({
        campaignName: isEncrypted.current
          ? decryptedData(transaction?.campaignName)
          : transaction?.campaignName,
        investorName: `${
          isEncrypted.current
            ? decryptedData(transaction?.investorFirstName)
            : transaction?.investorFirstName
        } ${
          isEncrypted.current
            ? decryptedData(transaction?.investorLastName)
            : transaction?.investorLastName
        }`,
        purchaseType:
          transaction?.purchaseType === 'SECONDARY'
            ? 'Secondary Market Investment'
            : 'Normal Investment',
        preapprovalTotalAmount: formatCurrency(
          transaction?.preapprovalTotalAmount ?? 0,
          generalSettings.currencySymbolSide,
          transaction?.equityCurrencySymbol,
          transaction?.equityCurrencyCode,
          generalSettings.decimalPoints,
        ),
        earnedFee: formatCurrency(
          transaction?.earnedFee ?? 0,
          generalSettings.currencySymbolSide,
          transaction?.equityCurrencySymbol,
          transaction?.equityCurrencyCode,
          generalSettings.decimalPoints,
        ),
        earnedFeesPercentage:
          transaction?.earnedFeeDetail?.earnedFeesPercentage,
        earnedFlatFees: transaction?.earnedFeeDetail?.earnedFlatFees,
        gatewayName: transaction?.gatewayName,

        paymentConfirmationId: transaction?.isCryptoProject
          ? transaction?.paymentConfirmationId
          : isEncrypted.current
            ? decryptedData(
                transaction?.gatewayName !== 'Offline'
                  ? transaction?.paymentConfirmationId
                  : transaction?.preapprovalKey,
              )
            : transaction?.gatewayName !== 'Offline'
              ? transaction?.paymentConfirmationId
              : transaction?.preapprovalKey,
        preapprovalStatus: transaction?.preapprovalStatus,
        createdAt: transaction?.createdAt ?? '',
      }));
      setCsvFrontTransactionList(result);
    }
  }, [exportTransactionsList]);

  useEffect(() => {
    if (transactionInfo?.campaignId?.isCryptoProject) {
      dispatch(actions.getPaymentGateway('crypto-payment'));
    }
  }, [dispatch]);

  const transactionBaseUrl = (transactionInfo) => {
    const currentChain = payments?.find(
      (payment) =>
        payment.currencyCode ===
        transactionInfo?.campaignId?.equityCurrencyCode,
    );

    return `${currentChain?.transactionBaseUrl}${transactionInfo?.paymentConfirmationId}`;
  };

  const refundInvestment = (data) => {
    if (!isConnected) {
      open();
      return;
    } else {
      const currentChain = payments?.find(
        (payment) =>
          payment.currencyCode === data?.campaignId?.equityCurrencyCode,
      );

      interact(
        currentChain?.contractAddress,
        abi,
        'refundInvestment',
        [
          data?.campaignId?.projectId,
          data?.user?.walletAddress,
          ethers.parseEther(String(data.amount)),
        ],
        currentChain?.chainId,
        address,
        '0',
      );
    }
  };

  useEffect(() => {
    if (updatedTransactionData) {
      toast.success(
        'Investment Refunded successfully. Please wait it may take some time.',
      );
      setTimeout(
        () => dispatch(getTransactions(showMore, activeFilters)),
        1000,
      );
    }
  }, [updatedTransactionData]);

  const sortOptions = {
    companyName: (row) => row?.companyId?.companyName?.toLowerCase?.() || '',
    'user.firstName': (row) =>
      `${row?.user?.firstName || ''} ${
        row?.user?.lastName || ''
      }`.toLowerCase(),
    amount: (row) => row?.preapprovalTotalAmount || 0,
    earnedFee: (row) => row?.earnedFee || 0,
    createdAt: (row) => row?.createdAt || '',
    preapprovalStatus: (row) => row?.preapprovalStatus || '',
  };

  const sortedTransactions = transactions?.docs
    ? sortData(transactions?.docs, sortConfig, sortOptions)
    : [];

  const handleSort = (column, direction) => {
    setSortConfig({ column, direction });
  };

  const transactionList =
    sortedTransactions?.length > 0 ? (
      sortedTransactions?.map((transaction, key) => (
        <tr key={key}>
          <td>
            <a
              href={`${siteBaseUrl.current}campaign-detail-page/${transaction?.companyId?.companySlug}`}
              target="_blank"
              className="text-secondary"
              rel="noopener noreferrer"
            >
              {transaction?.campaignId?.campaignType === 'realestate'
                ? decryptedData(transaction?.campaignId?.propertyName)
                : decryptedData(transaction?.companyId?.companyName)}
            </a>
          </td>
          <td>
            <a
              href={`${siteBaseUrl.current}profile/${transaction.user?.slug}`}
              target="_blank"
              className="text-secondary"
              rel="noopener noreferrer"
            >
              {isEncrypted.current
                ? decryptedData(transaction?.user?.firstName)
                : transaction?.user?.firstName}{' '}
              {isEncrypted.current
                ? decryptedData(transaction?.user?.lastName)
                : transaction?.user?.lastName}
            </a>
          </td>
          <td>
            {transaction?.campaignId?.isCryptoProject ? (
              <CurrencyLogo
                symbol={transaction?.campaignId?.equityCurrencySymbol}
                variant='dark'
                height={16}
              />
            ) : null}
            {formatCurrency(
              transaction.preapprovalTotalAmount ?? 0,
              generalSettings.currencySymbolSide,
              transaction?.campaignId?.equityCurrencySymbol,
              transaction.campaignId?.equityCurrencyCode,
              generalSettings.decimalPoints,
            )}
          </td>
          <OverlayTrigger
            key={key}
            placement={'right'}
            overlay={
              <Tooltip>
                <span>
                  Revenue:
                  <b> {transaction?.earnedFee ?? 0}</b>
                </span>
                <br />
                <span>
                  Earned Fees:
                  <b>
                    {' '}
                    {transaction?.earnedFeeDetail?.earnedFeesPercentage} (%)
                  </b>
                </span>
                <br />
                <span>
                  Earned Flat Fees:
                  <b> {transaction?.earnedFeeDetail?.earnedFlatFees}</b>
                </span>
                {transaction?.gatewayId?.paymentType === 'plaid-dwolla' && (
                  <>
                    <br />
                    <span>
                      Gateway Fees:
                      <b> {transaction?.feesDetails?.feesPercentage} (%)</b>
                    </span>
                    <br />
                    <span>
                      Gateway Flat Fees:
                      <b> {transaction?.feesDetails?.flatFees}</b>
                    </span>
                  </>
                )}
              </Tooltip>
            }
          >
            <td>
              {transaction?.campaignId?.isCryptoProject ? (
                <CurrencyLogo
                  symbol={transaction?.campaignId?.equityCurrencySymbol}
                  variant='dark'
                  height={16}
                />
              ) : null}
              {formatCurrency(
                transaction.earnedFee ?? 0,
                generalSettings.currencySymbolSide,
                transaction?.campaignId?.equityCurrencySymbol,
                transaction.campaignId?.equityCurrencyCode,
                generalSettings.decimalPoints,
              )}
            </td>
          </OverlayTrigger>
          <td>
            {moment(transaction.createdAt).format(generalSettings.dateFormat) ??
              '-'}
            <br />
            {transaction.createdAt &&
              new Date(transaction.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
          </td>
          <td>
            <span
              className={`label d-flex w-100 justify-content-center label-lg ${
                transaction.preapprovalStatus === 'SUCCESS'
                  ? 'label-light-success'
                  : transaction.preapprovalStatus === 'PENDING'
                    ? 'label-light-secondary'
                    : 'label-light-refunded'
              } label-inline`}
            >
              {transaction.preapprovalStatus === 'SUCCESS'
                ? 'Success'
                : transaction.preapprovalStatus === 'PENDING'
                  ? 'Pending'
                  : 'Refunded'}
            </span>
          </td>
          {demoMode === 'false' && currentPermissions?.view_transaction ? (
            <td className="text-start">
              <div className="w-68">
                <OverlayTrigger
                  popperConfig={{
                    modifiers: {
                      preventOverflow: {
                        enabled: false,
                      },
                      hide: {
                        enabled: false,
                      },
                    },
                  }}
                  placement="bottom"
                  overlay={<Tooltip>View</Tooltip>}
                >
                  <button
                    onClick={() => showTransactionDetail(transaction)}
                    id={`kt_view_transaction_toggle_${key}`}
                    className="action-btn-secondary me-2"
                  >
                    <span className="btn-svg">
                      <SVG
                        title=' '
                        src={toAbsoluteUrl(
                          'media/svg/icons/General/Visible.svg',
                        )}
                      />
                    </span>
                  </button>
                </OverlayTrigger>
                {/* {currentPermissions?.refund_transaction &&
                !transaction?.campaignId?.termsSlug.includes('tokenization') ? (
                  <>
                    {transaction?.campaignId?.status === 2 &&
                    transaction?.paymentStatus !== 2 ? (
                      <OverlayTrigger
                        popperConfig={{
                          modifiers: {
                            preventOverflow: {
                              enabled: false,
                            },
                            hide: {
                              enabled: false,
                            },
                          },
                        }}
                        placement="bottom"
                        overlay={<Tooltip>Refund</Tooltip>}
                      >
                        {transaction.campaignId?.isCryptoProject ? (
                          <button
                            className='action-btn-warning'
                            disabled={
                              isReconnecting ||
                              isConnecting ||
                              isLoading ||
                              transaction.preapprovalStatus === 'REFUNDED'
                            }
                            hidden={isSuccess}
                            onClick={() => refundInvestment(transaction)}
                          >
                            <span className="btn-svg">
                              <SVG
                                title=' '
                                src={toAbsoluteUrl(
                                  'media/svg/icons/General/refund.svg',
                                )}
                              />
                            </span>
                          </button>
                        ) : (
                          <button
                            disabled={
                              transaction.preapprovalStatus === 'REFUNDED' ||
                              transaction.preapprovalStatus === 'PENDING'
                            }
                            onClick={() => refundShow(transaction)}
                            id="kt_view_transaction_toggle"
                            className="action-btn-warning"
                          >
                            <span className="btn-svg">
                              <SVG
                                title=' '
                                src={toAbsoluteUrl(
                                  'media/svg/icons/General/refund.svg',
                                )}
                              />
                            </span>
                          </button>
                        )}
                      </OverlayTrigger>
                    ) : null}
                  </>
                ) : null} */}
              </div>
            </td>
          ) : null}
        </tr>
      ))
    ) : (
      <tr className='text-center'>
        <td colSpan={7}>
          {transactions?.docs?.length === 0 ? (
            <span>
              <img
                height={120}
                src={toAbsoluteUrl('media/animations/nodata-animation.gif')}
                alt="no data found!"
              />
              <br />
              <span className='text-primary fs-6 fw-bold'>
                No Data Available!
              </span>
            </span>
          ) : (
            'Loading...'
          )}
        </td>
      </tr>
    );
  if (transactionList?.length < 1) {
    return (
      <>
        <LoadingDialog />
      </>
    );
  } else {
    return (
      <>
        {transactions?.totalAmountWiseDetail?.length > 0 &&
        currentPermissions?.list_total_amount ? (
          <TotalTransaction
            totalAmountWiseDetail={transactions?.totalAmountWiseDetail}
          ></TotalTransaction>
        ) : null}
        <FormProvider {...methods}>
          <Form
            className="my-3"
            name="searchTransaction"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <SearchTransaction clearData={setActiveFilters}></SearchTransaction>
          </Form>
        </FormProvider>

        <Card>
          <CardHeader title='Transactions'>
            <CardHeaderToolbar>
              {currentPermissions?.export_transactions && (
                <CSVLink
                  data={csvFrontTransactionList}
                  headers={transactionCSVHeaders}
                  className='btn font-size-sm btn-secondary fw-bold text-white py-1-5 me-3'
                  disabled={exportLoading}
                  filename={'Transaction List.csv'}
                >
                  <SVG
                    height={15}
                    className='me-2'
                    width={15}
                    src={toAbsoluteUrl(
                      'media/svg/icons/General/export-csv.svg',
                    )}
                  />
                  {exportLoading ? 'Loading..' : 'Export CSV'}
                </CSVLink>
              )}
              {currentPermissions?.add_transaction && (
                <button
                  type="button"
                  className="btn font-size-sm py-2 add-new-item-btn"
                  onClick={() => navigate(`/transaction/add-transaction`)}
                >
                  Add Transaction
                </button>
              )}
            </CardHeaderToolbar>
          </CardHeader>

          <CardBody className='px-4 pb-6 pt-0'>
            <div className='sticky-table-header-wrapper'>
              <Table className='table-head-custom table-custom-bg table-vertical-center'>
                <thead>
                  <tr>
                    <th>
                      <SortDropdownHeader
                        columnKey='companyName'
                        onSort={handleSort}
                        currentSort={sortConfig}
                      >
                        Company
                      </SortDropdownHeader>
                    </th>
                    <th>
                      <SortDropdownHeader
                        columnKey='user.firstName'
                        onSort={handleSort}
                        currentSort={sortConfig}
                      >
                        User
                      </SortDropdownHeader>
                    </th>
                    <th>
                      <SortDropdownHeader
                        columnKey='amount'
                        onSort={handleSort}
                        currentSort={sortConfig}
                      >
                        Amount
                      </SortDropdownHeader>
                    </th>
                    <th>
                      <SortDropdownHeader
                        columnKey='earnedFee'
                        onSort={handleSort}
                        currentSort={sortConfig}
                      >
                        Earned Fee
                      </SortDropdownHeader>
                    </th>
                    <th>
                      <SortDropdownHeader
                        columnKey='createdAt'
                        onSort={handleSort}
                        currentSort={sortConfig}
                      >
                        Date
                      </SortDropdownHeader>
                    </th>
                    <th>
                      <SortDropdownHeader
                        columnKey='preapprovalStatus'
                        onSort={handleSort}
                        align="right"
                        currentSort={sortConfig}
                      >
                        Status
                      </SortDropdownHeader>
                    </th>

                    {demoMode === 'false' ? (
                      <th className="width110">Action</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>{transactionList}</tbody>
              </Table>

              {transactions?.displayLoadMore ? (
                <div className='mt-5 text-center'>
                  <Button
                    type='button'
                    variant='primary'
                    className='btn btn-dark py-2'
                    onClick={() => loadMore()}
                    disabled={isValidating}
                  >
                    {isValidating ? (
                      <Spinner
                        variant="light"
                        style={{ height: '1.2rem', width: '1.2rem' }}
                      />
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              ) : null}
            </div>
          </CardBody>
        </Card>

        <Modal
          centered
          show={show}
          onHide={handleClose}
          size="lg"
        >
          <Modal.Header className="pb-2 px-6">
            <Modal.Title className="font-size-xl fw-bold text-secondary">
              <Link
                className="text-decoration-none text-secondary"
                to={`/campaign/campaign-details/${transactionInfo?.campaignId?._id}`}
              >
                {isEncrypted.current
                  ? decryptedData(transactionInfo?.companyId?.companyName)
                  : transactionInfo?.companyId?.companyName}
              </Link>
            </Modal.Title>
            <button
              type="button"
              className="btn btn-sm btn-dark py-1 btn-hover-dark ms-auto"
              onClick={handleClose}
            >
              Close
            </button>
          </Modal.Header>
          <Modal.Body className='p-6 rounded-fix'>
            <div className='offcanvas-content p-4 rounded bg-table-bg-clr'>
              <h6 className='text-black fw-bold mb-4'>Transaction Detail</h6>
              <ul className='p-0 m-0'>
                <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3'>
                  <span>Investor Name</span>
                  <strong className='ms-4 text-primary fw-medium'>
                    {isEncrypted.current
                      ? decryptedData(transactionInfo?.user?.firstName)
                      : transactionInfo?.user?.firstName}{' '}
                    {isEncrypted.current
                      ? decryptedData(transactionInfo?.user?.lastName)
                      : transactionInfo?.user?.lastName}
                  </strong>
                </li>

                {generalSettings?.plaidDwollaModule === 'yes' && (
                  <>
                    <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3'>
                      <span>Investor Funding Source ID</span>
                      <strong className='ms-4 text-primary fw-medium'>
                        {isEncrypted.current
                          ? decryptedData(
                              transactionInfo?.investorFundingSourceId,
                            )
                          : transactionInfo?.investorFundingSourceId}
                      </strong>
                    </li>
                    <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3'>
                      <span>Campaign Owner Funding Source ID</span>
                      <strong className='ms-4 text-primary fw-medium'>
                        {isEncrypted.current
                          ? decryptedData(
                              transactionInfo?.campaignOwnerFundingSourceId,
                            )
                          : transactionInfo?.campaignOwnerFundingSourceId}
                      </strong>
                    </li>
                  </>
                )}

                <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3'>
                  <span>Gateway Name</span>
                  <PaymentGatewayLogo
                    gatewayName={
                      transactionInfo?.gatewayId?.paymentType !==
                      'crypto-payment'
                        ? transactionInfo?.gatewayId?.paymentType
                        : transactionInfo?.campaignId?.equityCurrencyCode
                    }
                    doneFromWallet={transactionInfo?.doneFromWallet}
                  />
                </li>
                {transactionInfo?.doneFromWallet === 'partial' ||
                transactionInfo?.doneFromWallet === 'full' ? (
                  <li className="d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3">
                    <span>Wallet ID</span>
                    <strong className='ms-4 text-primary fw-medium'>
                      {transactionInfo?.walletId}
                    </strong>
                  </li>
                ) : null}

                {transactionInfo?.paymentConfirmationId ? (
                  <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3'>
                    <span>Gateway Transaction ID</span>
                    {transactionInfo?.campaignId?.isCryptoProject ? (
                      <a
                        target="_blank"
                        className="text-secondary fw-medium"
                        href={transactionBaseUrl(transactionInfo)}
                      >
                        <strong>{`${transactionInfo?.paymentConfirmationId?.substring(
                          0,
                          5,
                        )}...${transactionInfo?.paymentConfirmationId?.substring(
                          transactionInfo?.paymentConfirmationId.length - 5,
                          transactionInfo?.paymentConfirmationId.length,
                        )}`}</strong>
                      </a>
                    ) : (
                      <strong className='ms-4 text-primary fw-medium'>
                        {transactionInfo?.paymentConfirmationId
                          ? isEncrypted.current
                            ? decryptedData(
                                transactionInfo?.paymentConfirmationId,
                              )
                            : transactionInfo?.paymentConfirmationId
                          : '-'}
                      </strong>
                    )}
                  </li>
                ) : null}

                {transactionInfo?.gatewayId?.title === 'Offline' ? (
                  <li className="d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3">
                    <span>Transaction ID</span>
                    <strong className='ms-4 text-primary fw-medium'>
                      {transactionInfo?.preapprovalKey
                        ? isEncrypted.current
                          ? decryptedData(transactionInfo?.preapprovalKey)
                          : transactionInfo?.preapprovalKey
                        : '-'}
                    </strong>
                  </li>
                ) : null}

                <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3'>
                  <span>Amount</span>
                  <strong className='ms-4 text-primary fw-medium  d-flex align-items-center gap-1'>
                    <span>
                      {transactionInfo?.gatewayId?.paymentType !==
                      'crypto-payment' ? (
                        transactionInfo?.campaignId?.equityCurrencySymbol
                      ) : (
                        <CurrencyLogo
                          symbol={
                            transactionInfo?.campaignId?.equityCurrencySymbol
                          }
                          variant='primary'
                          height={18}
                        />
                      )}
                    </span>

                    {transactionInfo?.preapprovalTotalAmount}
                  </strong>
                </li>
                <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark mb-3'>
                  <span>Status</span>
                  <strong className='ms-4 text-primary fw-medium'>
                    <span
                      className={`label label-lg py-0 font-size-sm ${
                        transactionInfo?.preapprovalStatus === 'SUCCESS'
                          ? 'label-light-success'
                          : transactionInfo?.preapprovalStatus === 'PENDING'
                            ? 'label-light-secondary'
                            : 'label-light-refunded'
                      } label-inline`}
                    >
                      {transactionInfo?.preapprovalStatus === 'SUCCESS'
                        ? 'Success'
                        : transactionInfo?.preapprovalStatus === 'PENDING'
                          ? 'Pending'
                          : 'Refunded'}
                    </span>
                  </strong>
                </li>
                <li className='d-flex justify-content-between rounded-fix bg-white py-2 px-3 text-dark'>
                  <span>Transaction Date</span>
                  <strong className='ms-4 text-primary fw-medium'>
                    {moment(transactionInfo?.createdAt).format(
                      generalSettings?.dateFormat,
                    ) ?? '-'}
                  </strong>
                </li>
              </ul>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          centered
          show={refund}
          onHide={refundClose}
        >
          <Form
            id="dealTypeSettings"
            className="form form-label-right"
            onSubmit={handleSubmit(onSubmitRefund)}
          >
            <Modal.Header className="pb-2">
              <Modal.Title className="font-size-xl fw-bold text-black">
                Refund
              </Modal.Title>
              <button
                type="button"
                className="btn btn-sm btn-dark py-1 btn-hover-dark ms-auto"
                onClick={refundClose}
              >
                Close
              </button>
            </Modal.Header>
            <Modal.Body className='bg-table-bg-clr p-4 rounded m-4'>
              <div
                className={`form-group bg-white rounded floating-input-field-wrapper pt-0 ${
                  errors.refundReason ? 'border border-danger border-1' : ''
                }`}
              >
                <label>
                  <label className='floating-input-field-label-static start-0 ms-4'>
                    Write a reason why you want refund?
                    <span className='text-important'> *</span>
                  </label>
                </label>
                <Form.Control
                  name='refundReason'
                  as='textarea'
                  rows='6'
                  className={classNames(
                    'form-control bg-transparent border-0 pt-0 text-black',
                  )}
                  {...register('refundReason', {
                    required: intl.formatMessage({ id: 'COMMON.FIELD.REQ' }),
                    pattern: {
                      value: /^[^\s]+(\s+[^\s]+)*$/,
                      message: 'Please enter valid refund reason',
                    },
                  })}
                />
              </div>
              <ErrorMessage
                errors={errors}
                name='refundReason'
                render={({ message }) => (
                  <div className='invalid-feedback d-block'>{message}</div>
                )}
              />
            </Modal.Body>
            <Modal.Footer className='border-top-0 p-4'>
              <Button
                type="button"
                className="bg-transparent border-0 text-text-clr font-size-sm fw-medium py-2 me-4"
                variant="secondary"
                onClick={refundClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-secondary py-2 border-0 px-6 fw-medium font-size-sm"
                variant="primary"
              >
                Submit
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </>
    );
  }
}

export default Transaction;
